import { TeamModel } from "../models/teamModel";
import { ErrorCode, TeamJoinError } from "../models/errorModel";
import { callFunction } from "./callFunction";
import * as axios from "axios";

/**
 * Retrieves tasks from the server.
 * @returns An array of TeamModel objects.
 */
export async function getTeams(all: boolean): Promise<TeamModel[]> {
  try {

    let params: { [key: string]: string } = {};
    params.graphType = "team";
    if (all) {
      params["all"] = "true";
    }
    const respData = await callFunction("GET", "callTeamsMgmtApi", params);
    return respData["teamResult"];
  } catch (e) {
    throw e;
  }
}

/**
 * Joins a team
 * @returns A team deep-link if successful.
 */
export async function joinTeam(teamId : string): Promise<any> {
  try {
    // Post to GraphAPI to join a team
    const respData = await callFunction("POST", "callGraph", { graphType: "team" }, { teamId: teamId });
    return respData;
  } catch (err: any) {
    // Handle timeout errors
    if (err.message && err.message.includes("timed out")) {
      throw new TeamJoinError(
        ErrorCode.TIMEOUT,
        "The request took too long. Please check your connection and try again.",
        err.message
      );
    }

    // Handle Axios errors from the API
    if (axios.default.isAxiosError(err)) {
      const errorCode = err.response?.data?.errorCode;
      const errorMessage = err.response?.data?.error;
      const details = err.response?.data?.details;

      // Map backend error codes to frontend errors
      switch (errorCode) {
        case "PERMISSION_DENIED":
          throw new TeamJoinError(
            ErrorCode.PERMISSION_DENIED,
            "Access denied. You may need additional permissions to join this team. Contact your IT administrator.",
            details
          );
        case "ALREADY_MEMBER":
          throw new TeamJoinError(
            ErrorCode.ALREADY_MEMBER,
            "Good news! You're already a member of this team.",
            details
          );
        case "TEAM_NOT_FOUND":
          throw new TeamJoinError(
            ErrorCode.TEAM_NOT_FOUND,
            "This team no longer exists or has been archived.",
            details
          );
        default:
          // Check for network errors
          if (err.message === "Network Error" || !err.response) {
            throw new TeamJoinError(
              ErrorCode.NETWORK_ERROR,
              "Unable to connect. Please check your internet connection and try again.",
              err.message
            );
          }

          // Unknown error
          throw new TeamJoinError(
            ErrorCode.UNKNOWN,
            errorMessage || "Something went wrong. Please try again or contact support.",
            details || err.message
          );
      }
    }

    // For any other error type
    throw new TeamJoinError(
      ErrorCode.UNKNOWN,
      "An unexpected error occurred. Please try again.",
      String(err)
    );
  }
}

/**
 * Lists joined teams
 * @returns A list of team ids.
 */
export async function getJoinedTeams(): Promise<TeamModel[]> {
  try {
    // Post to GraphAPI to join a team
    const respData = await callFunction("GET", "callGraph", { graphType: "team" });
    return respData["teamResult"];
  } catch (e) {
    throw e;
  }
}

export async function getIcon(teamId : string) : Promise<any> {
  try {
    const respData = await callFunction("GET", "callGraph", { graphType: "icon", teamId: teamId});

    return respData.iconResult;
  } catch (e) {
    throw e;
  }
}

// --- Batched, cached team-icon loading -------------------------------------
// Replaces the old one-request-per-team pattern. Icons are cached in
// localStorage so repeat loads are instant; uncached teams are resolved in a
// single batched backend call (which itself uses Microsoft Graph $batch).

const ICON_CACHE_PREFIX = "teamIcon:v2:"; // bump to invalidate any previously-cached (incl. stale null) entries
const ICON_TTL_MS = 12 * 60 * 60 * 1000; // 12h for real icons
const NULL_TTL_MS = 3 * 60 * 60 * 1000; //  3h for "no icon" (so new photos appear later)

function readCachedIcon(teamId: string): { data: string | null } | undefined {
  try {
    const raw = localStorage.getItem(ICON_CACHE_PREFIX + teamId);
    if (!raw) return undefined;
    const { data, expiresAt } = JSON.parse(raw);
    if (typeof expiresAt !== "number" || Date.now() > expiresAt) {
      localStorage.removeItem(ICON_CACHE_PREFIX + teamId);
      return undefined;
    }
    return { data };
  } catch {
    return undefined;
  }
}

function writeCachedIcon(teamId: string, data: string | null): void {
  try {
    const ttl = data ? ICON_TTL_MS : NULL_TTL_MS;
    localStorage.setItem(
      ICON_CACHE_PREFIX + teamId,
      JSON.stringify({ data, expiresAt: Date.now() + ttl })
    );
  } catch {
    // localStorage unavailable or over quota — caching is best-effort, ignore.
  }
}

/**
 * Synchronously returns the icons already cached in localStorage (hits only;
 * uncached teams are omitted). Used to seed the initial render so a reload shows
 * cached icons immediately, without racing the widget's own state initialization.
 */
export function getCachedIcons(teamIds: string[]): { [teamId: string]: string | null } {
  const out: { [teamId: string]: string | null } = {};
  for (const id of teamIds) {
    if (!id) continue;
    const cached = readCachedIcon(id);
    if (cached) out[id] = cached.data;
  }
  return out;
}

/**
 * Resolves icons for many teams at once. Returns a map of teamId -> data URI
 * (or null when the team has no icon). Cached teams are served from
 * localStorage; the rest are fetched in a single batched backend call.
 */
export async function getBatchIcons(teamIds: string[]): Promise<{ [teamId: string]: string | null }> {
  const result: { [teamId: string]: string | null } = {};
  const misses: string[] = [];

  for (const id of teamIds) {
    if (!id) continue;
    const cached = readCachedIcon(id);
    if (cached) result[id] = cached.data;
    else if (!misses.includes(id)) misses.push(id);
  }

  if (misses.length > 0) {
    try {
      const respData = await callFunction("POST", "callGraph", { graphType: "iconBatch" }, { teamIds: misses });
      const fetched: { [teamId: string]: string | null } = respData?.iconResults ?? {};
      for (const id of misses) {
        const data = fetched[id] ?? null;
        writeCachedIcon(id, data);
        result[id] = data;
      }
    } catch (e) {
      // Icon loading is non-critical: leave misses unset so the UI shows placeholders.
      console.error("getBatchIcons failed", e);
      for (const id of misses) result[id] = null;
    }
  }

  return result;
}
